import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, Play, Download, Film, Music2, Image as ImageIcon, Sparkles, FileText, Archive } from "lucide-react";
import { useMember } from "@/contexts/MemberAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  exclusive_song: { icon: Music2, label: "Exclusive Song" },
  behind_scenes: { icon: Film, label: "Behind the Scenes" },
  studio_session: { icon: Music2, label: "Studio Session" },
  unreleased: { icon: Music2, label: "Unreleased" },
  wallpaper: { icon: ImageIcon, label: "Wallpaper" },
  commentary: { icon: Play, label: "Commentary" },
  early_access: { icon: Sparkles, label: "Early Access" },
};

const KIND_ICON = { audio: Music2, video: Film, image: ImageIcon, pdf: FileText, zip: Archive, other: FileText };

function humanSize(n) {
  if (!n) return "";
  const u = ["B", "KB", "MB", "GB"]; let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
}

export default function Vault() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { member } = useMember();

  useEffect(() => {
    const headers = {};
    const tok = (typeof localStorage !== "undefined") ? localStorage.getItem("member_token") : null;
    if (tok) headers.Authorization = `Bearer ${tok}`;
    axios.get(`${API}/vault`, { headers }).then((r) => { setItems(r.data || []); setLoading(false); });
  }, [member]);

  const unlocked = items.filter((i) => i.unlocked);
  const locked = items.filter((i) => !i.unlocked);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] mb-3">MEMBERS ONLY</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-3">
            The <span className="text-[#D4AF37]">Vault</span>
          </h1>
          <p className="text-white/50 text-sm max-w-xl mx-auto">
            Unreleased tracks, studio sessions, behind-the-scenes footage, and exclusive wallpapers.
            Unlock more by leveling up your tier or earning points.
          </p>
          {!member && (
            <div className="mt-6 inline-flex gap-2">
              <Link to="/join" data-testid="vault-join-cta" className="text-xs px-5 py-2 bg-[#D4AF37] text-black hover:bg-white uppercase tracking-widest">Join to Unlock</Link>
              <Link to="/login" className="text-xs px-5 py-2 border border-white/20 text-white hover:border-[#D4AF37] uppercase tracking-widest">Sign In</Link>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-white/40 text-center">Loading vault…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-center border border-white/5 rounded-lg p-12" data-testid="vault-empty">
            No vault items yet. Check back soon — Luxifer 6 drops content here for members only.
          </div>
        ) : (
          <>
            {unlocked.length > 0 && (
              <section className="mb-12">
                <h2 className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">Your Access · {unlocked.length}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {unlocked.map((it) => (
                    <VaultCard
                      key={it.id}
                      item={it}
                      unlocked
                      expanded={expanded === it.id}
                      onToggle={() => setExpanded(expanded === it.id ? null : it.id)}
                    />
                  ))}
                </div>
              </section>
            )}
            {locked.length > 0 && (
              <section>
                <h2 className="text-[10px] uppercase tracking-widest text-white/40 mb-4">Locked · {locked.length}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {locked.map((it) => <VaultCard key={it.id} item={it} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function VaultCard({ item, unlocked, expanded, onToggle }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.exclusive_song;
  const Icon = meta.icon;
  const cover = item.cover_url ? (item.cover_url.startsWith("http") ? item.cover_url : `${BASE}${item.cover_url}`) : null;
  return (
    <div
      data-testid={`vault-item-${item.id}`}
      className={`relative rounded-lg overflow-hidden border transition-all ${
        unlocked ? "border-[#D4AF37]/30 bg-[#0A0A0A] hover:border-[#D4AF37]" : "border-white/5 bg-[#0A0A0A]/40 opacity-70"
      }`}
    >
      <button
        onClick={unlocked ? onToggle : undefined}
        disabled={!unlocked}
        className={`w-full aspect-video bg-gradient-to-br from-black to-[#1a1a1a] relative overflow-hidden text-left ${unlocked ? "cursor-pointer" : ""}`}
      >
        {cover ? (
          <img src={cover} alt={item.title} className={`w-full h-full object-cover ${!unlocked ? "blur-sm scale-105" : ""}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-10 h-10 text-white/20" />
          </div>
        )}
        {!unlocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Lock className="w-8 h-8 text-[#D4AF37]" />
          </div>
        )}
        {unlocked && (item.files || []).length > 0 && (
          <span className="absolute bottom-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 bg-black/70 text-[#D4AF37] rounded">
            {item.files.length} files
          </span>
        )}
      </button>
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{meta.label}</p>
        <h3 className="text-white text-sm font-medium mt-1">{item.title}</h3>
        <p className="text-white/40 text-xs mt-1 line-clamp-2">{item.description}</p>
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/5">
          <span className="text-[10px] uppercase tracking-widest text-white/40">Tier: {item.required_tier}</span>
          {unlocked && (item.files || []).length > 0 && (
            <button onClick={onToggle} data-testid={`vault-toggle-${item.id}`}
                    className="text-xs text-[#D4AF37] hover:text-white flex items-center gap-1 uppercase tracking-widest">
              {expanded ? "Hide" : "Open"}
            </button>
          )}
        </div>
      </div>

      {unlocked && expanded && (
        <div className="border-t border-white/5 bg-black p-4 space-y-3" data-testid={`vault-expanded-${item.id}`}>
          {(item.files || []).length === 0 ? (
            <p className="text-white/40 text-xs">No files uploaded yet.</p>
          ) : (
            (item.files || []).map((f) => <FileRow key={f.id} file={f} />)
          )}
        </div>
      )}

      <style>{`.line-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }`}</style>
    </div>
  );
}

function FileRow({ file }) {
  const KindIcon = KIND_ICON[file.kind] || KIND_ICON.other;
  const streamUrl = file.stream_url ? `${BASE}${file.stream_url}` : "";
  const downloadUrl = file.download_url ? `${BASE}${file.download_url}` : "";
  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded p-3" data-testid={`vault-file-${file.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <KindIcon className="w-4 h-4 text-[#D4AF37]" />
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm truncate">{file.filename}</div>
          <div className="text-white/40 text-[11px]">{file.kind} · {humanSize(file.size)}</div>
        </div>
        {file.is_downloadable && downloadUrl && (
          <a href={downloadUrl} data-testid={`vault-download-${file.id}`}
             className="text-xs text-[#D4AF37] hover:text-white flex items-center gap-1 uppercase tracking-widest">
            <Download className="w-3 h-3" /> Save
          </a>
        )}
      </div>
      {file.kind === "video" && streamUrl && (
        <video controls preload="metadata" src={streamUrl} className="w-full rounded" data-testid={`vault-video-${file.id}`} />
      )}
      {file.kind === "audio" && streamUrl && (
        <audio controls preload="metadata" src={streamUrl} className="w-full" data-testid={`vault-audio-${file.id}`} />
      )}
      {file.kind === "image" && streamUrl && (
        <img src={streamUrl} alt={file.filename} className="w-full max-h-96 object-contain rounded" />
      )}
    </div>
  );
}
